import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Grid,
  Typography,
  Button,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';

// Icons
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HistoryIcon from '@mui/icons-material/History';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PieChartOutlineOutlinedIcon from '@mui/icons-material/PieChartOutlineOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';

// Components & Services
import Page from '../../components/Page';
import BrudCrumbs from '../../components/BreadCrumbs';
import CustomInput from '../../components/CustomInput';
import CustomDialog from '../../components/CustomDialogue';
import SearchNotFound from '../../components/SearchNotFound';
import { ReportApi } from '../../utils/Apis';
import { NOT_AVAILABLE } from '../Global/Constants';
import { fDateTime } from '../../utils/formatTime';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getHistoryReportList, getReportsList } from '../../store/Slice/Report/ReportSlice';
import { downloadApiData } from '../../store/Slice/Global/GlobalSlice';
import useReportSelector from '../../store/Slice/Report/ReportSelector';

// ----------------------------------------------------------------------
// Custom 3D-styled SVG Illustrations for each card
// ----------------------------------------------------------------------

const CardIllustrations = {
  barsBlue: (
    <svg width="60" height="46" viewBox="0 0 60 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="36" y="8" width="16" height="34" rx="4" fill="url(#blueGrad1)" />
      <rect x="18" y="18" width="14" height="24" rx="4" fill="url(#blueGrad2)" />
      <rect x="2" y="28" width="12" height="14" rx="3" fill="url(#blueGrad3)" />
      <defs>
        <linearGradient
          id="blueGrad1"
          x1="44"
          y1="8"
          x2="44"
          y2="42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#38bdf8" />
          <stop offset="1" stopColor="#0284c7" />
        </linearGradient>
        <linearGradient
          id="blueGrad2"
          x1="25"
          y1="18"
          x2="25"
          y2="42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#60a5fa" />
          <stop offset="1" stopColor="#2563eb" />
        </linearGradient>
        <linearGradient id="blueGrad3" x1="8" y1="28" x2="8" y2="42" gradientUnits="userSpaceOnUse">
          <stop stopColor="#93c5fd" />
          <stop offset="1" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
    </svg>
  ),
  dashboardGreen: (
    <svg width="60" height="46" viewBox="0 0 60 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect
        x="4"
        y="6"
        width="52"
        height="34"
        rx="6"
        fill="#f0fdf4"
        stroke="#86efac"
        strokeWidth="1.5"
      />
      <circle cx="12" cy="13" r="2.5" fill="#22c55e" />
      <circle cx="19" cy="13" r="2.5" fill="#86efac" />
      <rect x="10" y="22" width="10" height="14" rx="2" fill="url(#cfoGrad1)" />
      <rect x="24" y="18" width="10" height="18" rx="2" fill="url(#cfoGrad2)" />
      <rect x="38" y="25" width="10" height="11" rx="2" fill="url(#cfoGrad1)" />
      <defs>
        <linearGradient
          id="cfoGrad1"
          x1="15"
          y1="22"
          x2="15"
          y2="36"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4ade80" />
          <stop offset="1" stopColor="#16a34a" />
        </linearGradient>
        <linearGradient
          id="cfoGrad2"
          x1="29"
          y1="18"
          x2="29"
          y2="36"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#22c55e" />
          <stop offset="1" stopColor="#15803d" />
        </linearGradient>
      </defs>
    </svg>
  ),
  trendOrange: (
    <svg width="60" height="46" viewBox="0 0 60 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="38" y="10" width="14" height="32" rx="3" fill="url(#orangeGrad1)" />
      <rect x="20" y="20" width="14" height="22" rx="3" fill="url(#orangeGrad2)" />
      <rect x="4" y="28" width="12" height="14" rx="3" fill="url(#orangeGrad3)" />
      <path
        d="M6 24L22 14L34 20L48 6"
        stroke="#ea580c"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="48" cy="6" r="3" fill="#ea580c" />
      <defs>
        <linearGradient
          id="orangeGrad1"
          x1="45"
          y1="10"
          x2="45"
          y2="42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#fb923c" />
          <stop offset="1" stopColor="#ea580c" />
        </linearGradient>
        <linearGradient
          id="orangeGrad2"
          x1="27"
          y1="20"
          x2="27"
          y2="42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#fdba74" />
          <stop offset="1" stopColor="#f97316" />
        </linearGradient>
        <linearGradient
          id="orangeGrad3"
          x1="10"
          y1="28"
          x2="10"
          y2="42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#fed7aa" />
          <stop offset="1" stopColor="#fb923c" />
        </linearGradient>
      </defs>
    </svg>
  ),
  docRed: (
    <svg width="56" height="46" viewBox="0 0 56 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect
        x="6"
        y="4"
        width="34"
        height="38"
        rx="5"
        fill="#ffffff"
        stroke="#fecaca"
        strokeWidth="1.5"
      />
      <rect x="12" y="12" width="14" height="2.5" rx="1.25" fill="#f87171" />
      <rect x="12" y="18" width="22" height="2.5" rx="1.25" fill="#e2e8f0" />
      <rect x="12" y="24" width="18" height="2.5" rx="1.25" fill="#e2e8f0" />
      <circle cx="38" cy="32" r="10" fill="url(#redGrad1)" />
      <path
        d="M34 32L37 35L42 29"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient
          id="redGrad1"
          x1="38"
          y1="22"
          x2="38"
          y2="42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#f87171" />
          <stop offset="1" stopColor="#dc2626" />
        </linearGradient>
      </defs>
    </svg>
  ),
  bankPurple: (
    <svg width="58" height="46" viewBox="0 0 58 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M29 6L8 16V19H50V16L29 6Z" fill="url(#purpGrad1)" />
      <rect x="12" y="21" width="6" height="14" rx="1.5" fill="#c084fc" />
      <rect x="22" y="21" width="6" height="14" rx="1.5" fill="#c084fc" />
      <rect x="32" y="21" width="6" height="14" rx="1.5" fill="#c084fc" />
      <rect x="42" y="21" width="6" height="14" rx="1.5" fill="#c084fc" />
      <rect x="6" y="37" width="46" height="4" rx="2" fill="url(#purpGrad2)" />
      <defs>
        <linearGradient
          id="purpGrad1"
          x1="29"
          y1="6"
          x2="29"
          y2="19"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#a855f7" />
          <stop offset="1" stopColor="#7e22ce" />
        </linearGradient>
        <linearGradient
          id="purpGrad2"
          x1="29"
          y1="37"
          x2="29"
          y2="41"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#a855f7" />
          <stop offset="1" stopColor="#6b21a8" />
        </linearGradient>
      </defs>
    </svg>
  ),
  registerTeal: (
    <svg width="58" height="46" viewBox="0 0 58 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect
        x="6"
        y="5"
        width="34"
        height="37"
        rx="5"
        fill="#f0fdfa"
        stroke="#99f6e4"
        strokeWidth="1.5"
      />
      <circle cx="12" cy="14" r="2" fill="#14b8a6" />
      <rect x="17" y="12.5" width="16" height="2.5" rx="1.25" fill="#cbd5e1" />
      <circle cx="12" cy="22" r="2" fill="#14b8a6" />
      <rect x="17" y="20.5" width="16" height="2.5" rx="1.25" fill="#cbd5e1" />
      <circle cx="42" cy="30" r="11" fill="url(#tealGrad1)" />
      <text
        x="42"
        y="35"
        textAnchor="middle"
        fill="white"
        fontSize="13"
        fontWeight="bold"
        fontFamily="sans-serif"
      >
        ₹
      </text>
      <defs>
        <linearGradient
          id="tealGrad1"
          x1="42"
          y1="19"
          x2="42"
          y2="41"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2dd4bf" />
          <stop offset="1" stopColor="#0f766e" />
        </linearGradient>
      </defs>
    </svg>
  ),
  coinsGold: (
    <svg width="58" height="46" viewBox="0 0 58 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="28" cy="14" rx="14" ry="5.5" fill="#facc15" />
      <path d="M14 14V20C14 23 20 25.5 28 25.5C36 25.5 42 23 42 20V14" fill="#eab308" />
      <ellipse cx="28" cy="20" rx="14" ry="5.5" fill="#facc15" />
      <path d="M14 20V26C14 29 20 31.5 28 31.5C36 31.5 42 29 42 26V20" fill="#ca8a04" />
      <ellipse cx="28" cy="26" rx="14" ry="5.5" fill="#facc15" />
      <circle cx="44" cy="32" r="9" fill="url(#goldGrad)" />
      <text
        x="44"
        y="36"
        textAnchor="middle"
        fill="white"
        fontSize="11"
        fontWeight="bold"
        fontFamily="sans-serif"
      >
        ₹
      </text>
      <defs>
        <linearGradient
          id="goldGrad"
          x1="44"
          y1="23"
          x2="44"
          y2="41"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#fde047" />
          <stop offset="1" stopColor="#ca8a04" />
        </linearGradient>
      </defs>
    </svg>
  ),
  payoutAnalytics: (
    <svg width="58" height="46" viewBox="0 0 58 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="22" y="12" width="10" height="30" rx="3" fill="url(#payoutGrad1)" />
      <rect x="8" y="24" width="10" height="18" rx="3" fill="url(#payoutGrad2)" />
      <rect x="36" y="6" width="10" height="36" rx="3" fill="url(#payoutGrad1)" />
      <circle cx="45" cy="32" r="8" fill="#f59e0b" />
      <text
        x="45"
        y="36"
        textAnchor="middle"
        fill="white"
        fontSize="10"
        fontWeight="bold"
        fontFamily="sans-serif"
      >
        ₹
      </text>
      <defs>
        <linearGradient
          id="payoutGrad1"
          x1="27"
          y1="6"
          x2="27"
          y2="42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#60a5fa" />
          <stop offset="1" stopColor="#1d4ed8" />
        </linearGradient>
        <linearGradient
          id="payoutGrad2"
          x1="13"
          y1="24"
          x2="13"
          y2="42"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#93c5fd" />
          <stop offset="1" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
    </svg>
  ),
  ledgerPurple: (
    <svg width="58" height="46" viewBox="0 0 58 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect
        x="8"
        y="6"
        width="40"
        height="36"
        rx="6"
        fill="#faf5ff"
        stroke="#d8b4fe"
        strokeWidth="1.5"
      />
      <rect x="8" y="6" width="8" height="36" rx="3" fill="#a855f7" />
      <circle cx="23" cy="16" r="2" fill="#c084fc" />
      <rect x="28" y="14.5" width="14" height="2.5" rx="1" fill="#cbd5e1" />
      <circle cx="23" cy="24" r="2" fill="#c084fc" />
      <rect x="28" y="22.5" width="14" height="2.5" rx="1" fill="#cbd5e1" />
      <circle cx="23" cy="32" r="2" fill="#c084fc" />
      <rect x="28" y="30.5" width="14" height="2.5" rx="1" fill="#cbd5e1" />
    </svg>
  ),
  productCube: (
    <svg width="58" height="46" viewBox="0 0 58 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M29 6L47 16V28L29 38L11 28V16L29 6Z" fill="#dcfce7" />
      <path d="M29 6L47 16L29 26L11 16L29 6Z" fill="url(#cubeGrad1)" />
      <path d="M11 16L29 26V38L11 28V16Z" fill="url(#cubeGrad2)" />
      <path d="M47 16L29 26V38L47 28V16Z" fill="url(#cubeGrad3)" />
      <defs>
        <linearGradient
          id="cubeGrad1"
          x1="29"
          y1="6"
          x2="29"
          y2="26"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4ade80" />
          <stop offset="1" stopColor="#22c55e" />
        </linearGradient>
        <linearGradient
          id="cubeGrad2"
          x1="20"
          y1="16"
          x2="20"
          y2="38"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#16a34a" />
          <stop offset="1" stopColor="#15803d" />
        </linearGradient>
        <linearGradient
          id="cubeGrad3"
          x1="38"
          y1="16"
          x2="38"
          y2="38"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#22c55e" />
          <stop offset="1" stopColor="#16a34a" />
        </linearGradient>
      </defs>
    </svg>
  ),
  vendorRose: (
    <svg width="58" height="46" viewBox="0 0 58 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10 32C16 32 20 28 26 28C32 28 38 32 46 32"
        stroke="#fb7185"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx="34" cy="18" r="10" fill="url(#roseGrad)" />
      <text
        x="34"
        y="22.5"
        textAnchor="middle"
        fill="white"
        fontSize="12"
        fontWeight="bold"
        fontFamily="sans-serif"
      >
        ₹
      </text>
      <defs>
        <linearGradient id="roseGrad" x1="34" y1="8" x2="34" y2="28" gradientUnits="userSpaceOnUse">
          <stop stopColor="#f43f5e" />
          <stop offset="1" stopColor="#be123c" />
        </linearGradient>
      </defs>
    </svg>
  ),
  payableSettings: (
    <svg width="58" height="46" viewBox="0 0 58 46" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect
        x="6"
        y="6"
        width="34"
        height="36"
        rx="5"
        fill="#eff6ff"
        stroke="#bfdbfe"
        strokeWidth="1.5"
      />
      <circle cx="12" cy="14" r="2" fill="#3b82f6" />
      <rect x="17" y="13" width="16" height="2" rx="1" fill="#cbd5e1" />
      <circle cx="12" cy="22" r="2" fill="#3b82f6" />
      <rect x="17" y="21" width="16" height="2" rx="1" fill="#cbd5e1" />
      <circle cx="38" cy="30" r="9" fill="url(#setGrad)" />
      <path d="M38 25V35M33 30H43" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <defs>
        <linearGradient id="setGrad" x1="38" y1="21" x2="38" y2="39" gradientUnits="userSpaceOnUse">
          <stop stopColor="#60a5fa" />
          <stop offset="1" stopColor="#1d4ed8" />
        </linearGradient>
      </defs>
    </svg>
  )
};

// ----------------------------------------------------------------------
// Master Catalog of Standard Report Cards
// ----------------------------------------------------------------------

const DEFAULT_REPORT_CARDS = [
  {
    reportCode: 'EMPLOYEE_BUSINESS',
    reportName: 'Employee Bussiness Report',
    reportDescription: 'Top-level business performance report for employees.',
    icon: <PeopleAltOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#e0f2fe',
    iconColor: '#0284c7',
    illustration: CardIllustrations.barsBlue
  },
  {
    reportCode: 'CFO_DASHBOARD',
    reportName: 'CFO Dashboard',
    reportDescription: 'Dashboard view for financial tracking and key metrics.',
    icon: <PieChartOutlineOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#ecfdf5',
    iconColor: '#10b981',
    illustration: CardIllustrations.dashboardGreen
  },
  {
    reportCode: 'PIPELINE_FORECAST',
    reportName: 'Pipeline Forecast',
    reportDescription: 'Overview of current and upcoming loan cases.',
    icon: <TrendingUpOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#fff7ed',
    iconColor: '#f97316',
    illustration: CardIllustrations.trendOrange
  },
  {
    reportCode: 'SANCTION_DISBURSEMENT',
    reportName: 'Sanction and Disbursement',
    reportDescription: 'Report on sanctioned and disbursed loans.',
    icon: <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#fef2f2',
    iconColor: '#ef4444',
    illustration: CardIllustrations.docRed
  },
  {
    reportCode: 'BANK_WISE_MIS',
    reportName: 'Bank Wise MIS',
    reportDescription: 'Bank-level MIS report for finance tracking.',
    icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#f3e8ff',
    iconColor: '#a855f7',
    illustration: CardIllustrations.bankPurple
  },
  {
    reportCode: 'RECEIVABLE_REGISTER',
    reportName: 'Receivable Register',
    reportDescription: 'Detailed report of all receivables within the company.',
    icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#f0fdfa',
    iconColor: '#0d9488',
    illustration: CardIllustrations.registerTeal
  },
  {
    reportCode: 'BROKER_SALARY_PAYABLES',
    reportName: 'Broker and Salary Payables',
    reportDescription: 'Comprehensive view of broker commissions and salary payables.',
    icon: <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#fefce8',
    iconColor: '#eab308',
    illustration: CardIllustrations.coinsGold
  },
  {
    reportCode: 'BROKER_PAYOUT_ANALYTICS',
    reportName: 'Broker Payout Analytics',
    reportDescription: 'Summary of broker payouts including commissions and incentives.',
    icon: <BarChartOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#eff6ff',
    iconColor: '#3b82f6',
    illustration: CardIllustrations.payoutAnalytics
  },
  {
    reportCode: 'LEDGER_BRIDGE',
    reportName: 'Ledger Bridge',
    reportDescription:
      'Ledger report showing all debit and credit entries for financial reconciliation.',
    icon: <MenuBookOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#faf5ff',
    iconColor: '#9333ea',
    illustration: CardIllustrations.ledgerPurple
  },
  {
    reportCode: 'BANK_PRODUCT_MIS',
    reportName: 'Bank Product MIS',
    reportDescription: 'Product-wise performance and disbursement analysis.',
    icon: <WidgetsOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#ecfdf5',
    iconColor: '#10b981',
    illustration: CardIllustrations.productCube
  },
  {
    reportCode: 'VENDOR_PAYOUT',
    reportName: 'Vendor Payout',
    reportDescription: 'Vendor-wise payout details and status tracking.',
    icon: <VolunteerActivismOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#fff1f2',
    iconColor: '#f43f5e',
    illustration: CardIllustrations.vendorRose
  },
  {
    reportCode: 'PAYABLE_CONTROL',
    reportName: 'Payable Control',
    reportDescription: 'Track and manage all payables efficiently.',
    icon: <SettingsOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#eff6ff',
    iconColor: '#0284c7',
    illustration: CardIllustrations.payableSettings
  },
  {
    reportCode: 'BILLING_SHEET',
    reportName: 'Billing Sheet Report',
    reportDescription: 'Billing sheet of payments received in the selected period.',
    icon: <RequestQuoteOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: '#f0fdfa',
    iconColor: '#0d9488',
    illustration: CardIllustrations.registerTeal
  }
];

export default function Report() {
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { ReportListData, ReportHistoryData } = useReportSelector();

  const [downloadPopup, setDownloadPopup] = useState(false);
  const [historyPopup, setHistoryPopup] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const getReportApi = useCallback(() => {
    if (!LoginData) return;
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      reportFilterModel: {
        fileLoginedBankId: null,
        fileLoginedBranchId: null,
        dsaCode: null,
        loanTypeId: null,
        statusId: null,
        toLeadDate: null
      },
      searchAndPaginationModel: {
        searchLiteral: null,
        pageSize: 50,
        pageNumber: 1,
        sortOrder: 'asc'
      }
    };
    dispatch(getReportsList(credentials));
  }, [LoginData, dispatch]);

  useEffect(() => {
    getReportApi();
  }, [getReportApi]);

  // Merge API data with rich design configurations
  const displayReports = React.useMemo(() => {
    if (Array.isArray(ReportListData) && ReportListData.length > 0) {
      return ReportListData.map((item, index) => {
        const matchingConfig =
          DEFAULT_REPORT_CARDS.find(
            (c) =>
              c.reportCode === item.reportCode ||
              c.reportName.toLowerCase() === (item.reportName || '').toLowerCase()
          ) || DEFAULT_REPORT_CARDS[index % DEFAULT_REPORT_CARDS.length];

        return {
          ...matchingConfig,
          ...item,
          reportName: item.reportName || NOT_AVAILABLE,
          reportDescription: item.reportDescription || NOT_AVAILABLE,
          reportCode: item.reportCode,
          icon: matchingConfig.icon,
          iconBg: matchingConfig.iconBg,
          iconColor: matchingConfig.iconColor,
          illustration: matchingConfig.illustration
        };
      });
    }
    return [];
  }, [ReportListData]);

  // Handle download button click
  const handleDownloadClick = (report) => {
    setSelectedReport(report);
    downloadReset();
    setDownloadPopup(true);
  };

  // Handle history button click
  const handleHistoryClick = async (report) => {
    setSelectedReport(report);
    await dispatch(getHistoryReportList(report?.reportCode));
    setHistoryPopup(true);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryPopup(false);
    setSelectedReport(null);
  };

  const handleCloseDownloadDialog = () => {
    setDownloadPopup(false);
    setSelectedReport(null);
    downloadReset();
  };

  const {
    handleSubmit: handleDownloadSubmit,
    control: downloadControl,
    reset: downloadReset,
    formState: { errors: downloadErrors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      fromDate: '',
      toDate: ''
    }
  });

  const onDownloadSubmit = useCallback(
    async (data) => {
      setIsDownloading(true);
      const fromDateString = format(new Date(data.fromDate), 'yyyy-MM-dd');
      const toDateString = format(new Date(data.toDate), 'yyyy-MM-dd');
      try {
        const downloadCredentials = {
          companyId: LoginData?.companyId,
          userId: LoginData?.userId,
          fromDate: fromDateString,
          toDate: toDateString,
          loanTypeId: null,
          city: null
        };

        const result = await dispatch(
          downloadApiData(
            `${ReportApi?.reportDownload}/${selectedReport?.reportCode}`,
            downloadCredentials
          )
        );

        if (result?.success && result?.data) {
          const serverFileName =
            result?.headers?.['content-disposition']?.match(/filename="?([^";]+)"?/i)?.[1];
          const fileName =
            serverFileName ||
            `Report_${selectedReport?.reportCode}_Download_${format(
              new Date(),
              'dd-MM-yyyy_HH-mm-ss'
            )}.xlsx`;

          const url = window.URL.createObjectURL(result.data);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          handleCloseDownloadDialog();
        }
      } catch (error) {
        console.error('Export error:', error);
      } finally {
        setIsDownloading(false);
      }
    },
    [selectedReport, LoginData, dispatch]
  );

  return (
    <Page title="Partner Management System || Report Cards">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title, and Breadcrumbs */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          {/* Left Title with Icon */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <ArticleOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Report Cards
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Access and download detailed reports for better insights and decision making.
              </Typography>
            </Box>
          </Stack>

          {/* Right Breadcrumbs */}
          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        {/* 3-Column Report Cards Grid */}
        <Grid container spacing={3}>
          {displayReports.map((report, index) => (
            <Grid item xs={12} sm={6} md={4} key={report.reportCode || index}>
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: '16px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.03)',
                  bgcolor: '#ffffff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.25s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 28px rgba(2, 132, 199, 0.08)',
                    borderColor: '#cbd5e1'
                  }
                }}
              >
                {/* Card Top Section: Icon, Titles and Right Illustration */}
                <Box sx={{ mb: 2.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1.5
                    }}
                  >
                    {/* Left Icon + Text */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          minWidth: 42,
                          borderRadius: '10px',
                          bgcolor: report.iconBg || '#e0f2fe',
                          color: report.iconColor || '#0284c7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {report.icon || <ArticleOutlinedIcon sx={{ fontSize: 22 }} />}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '15px',
                            color: '#0f172a',
                            lineHeight: 1.25,
                            mb: 0.5
                          }}
                        >
                          {report.reportName}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: '#64748b',
                            lineHeight: 1.4
                          }}
                        >
                          {report.reportDescription}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Right Illustration */}
                    <Box sx={{ flexShrink: 0, pl: 0.5 }}>
                      {report.illustration || CardIllustrations.barsBlue}
                    </Box>
                  </Box>
                </Box>

                {/* Card Bottom Section: 2 Action Buttons */}
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleDownloadClick(report)}
                      startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        bgcolor: '#0284c7',
                        color: '#ffffff',
                        borderRadius: '10px',
                        py: 0.85,
                        fontWeight: 600,
                        fontSize: '13px',
                        textTransform: 'none',
                        boxShadow: '0 2px 8px rgba(2, 132, 199, 0.2)',
                        '&:hover': {
                          bgcolor: '#0369a1'
                        }
                      }}
                    >
                      Download
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => handleHistoryClick(report)}
                      startIcon={<HistoryIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        border: '1px solid #e2e8f0',
                        color: '#0284c7',
                        bgcolor: '#ffffff',
                        borderRadius: '10px',
                        py: 0.85,
                        fontWeight: 600,
                        fontSize: '13px',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#0284c7',
                          bgcolor: 'rgba(2, 132, 199, 0.04)'
                        }
                      }}
                    >
                      History
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          ))}
          {displayReports.length === 0 && (
            <Grid item xs={12}>
              <SearchNotFound />
            </Grid>
          )}
        </Grid>

        {/* Download Dialog */}
        <CustomDialog
          showCloseIcon
          open={downloadPopup}
          maxWidth="sm"
          onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              handleCloseDownloadDialog();
            }
          }}
          title={`Download ${selectedReport?.reportName || 'Report'}`}
          content={
            <Box mb={2} mt={1}>
              <form id="DownloadReportForm" onSubmit={handleDownloadSubmit(onDownloadSubmit)}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <CustomInput
                      name="fromDate"
                      label="From Date"
                      type="date"
                      control={downloadControl}
                      errors={downloadErrors}
                      isRequired
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomInput
                      name="toDate"
                      label="To Date"
                      type="date"
                      control={downloadControl}
                      errors={downloadErrors}
                      isRequired
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </Grid>
                </Grid>
              </form>
            </Box>
          }
          actions={
            <Box display="flex" justifyContent="flex-end" gap={1.5}>
              <Button
                variant="outlined"
                onClick={handleCloseDownloadDialog}
                disabled={isDownloading}
                sx={{
                  borderRadius: '8px',
                  borderColor: '#cbd5e1',
                  color: '#64748b',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="DownloadReportForm"
                variant="contained"
                disabled={isDownloading}
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#0284c7',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#0369a1' }
                }}
              >
                {isDownloading ? 'Downloading...' : 'Apply & Download'}
              </Button>
            </Box>
          }
        />

        {/* History Dialog */}
        <CustomDialog
          showCloseIcon
          open={historyPopup}
          maxWidth="lg"
          onClose={(event, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
              handleCloseHistoryDialog();
            }
          }}
          title={`Download History - ${selectedReport?.reportName || 'Report'}`}
          content={
            <Box mb={2} mt={1}>
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  boxShadow: 'none',
                  overflow: 'hidden'
                }}
              >
                <Table>
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12.5px', color: '#64748b' }}>
                        Sr.No.
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12.5px', color: '#64748b' }}>
                        Downloaded At
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12.5px', color: '#64748b' }}>
                        File Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12.5px', color: '#64748b' }}>
                        User Name
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {ReportHistoryData && ReportHistoryData.length > 0 ? (
                      ReportHistoryData.map((row, index) => (
                        <TableRow key={row.id || index} hover>
                          <TableCell sx={{ fontSize: '13px', color: '#334155' }}>
                            {index + 1}
                          </TableCell>
                          <TableCell sx={{ fontSize: '13px', color: '#334155' }}>
                            {fDateTime(row?.downloadedAt)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '13px', color: '#334155', fontWeight: 600 }}>
                            {row?.fileName}
                          </TableCell>
                          <TableCell sx={{ fontSize: '13px', color: '#334155' }}>
                            {row?.userName}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3, color: '#94a3b8' }}>
                          No download history found for this report.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          }
          actions={
            <Box display="flex" justifyContent="flex-end" gap={1.5}>
              <Button
                variant="outlined"
                onClick={handleCloseHistoryDialog}
                sx={{
                  borderRadius: '8px',
                  borderColor: '#cbd5e1',
                  color: '#64748b',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
                }}
              >
                Close
              </Button>
            </Box>
          }
        />
      </Container>
    </Page>
  );
}
